import * as React from 'react';
import qs from 'qs';
import { useNavigate, useLocation } from 'react-router-dom';
import { type Device } from 'src/utils/api/get-orgs-promise/types-and-schema';
import { FilterValues } from 'src/types';
import { TZ } from 'src/constants/others/timezones';
import { TaskStatus, TaskTypes } from 'src/utils';
import { type UserReportRangeType } from 'src/utils/api/retry-user-report/retry-user-report';
import { PARSE_OPTIONS, STRINGIFY_OPTIONS } from './constant';

export interface SearchParams {
  zone_type?: string;
  page?: string | number;
  search?: string;
  device?: Device;
  org_tab?: string | number;
  tableRows?: string[];
  filter?: FilterValues;
  sim_status?: string;
  date?: string;
  taskId?: string;
  log_type?: Array<string>;
  org_sort?: 'name' | 'image_date';
  user_report_range_type?: UserReportRangeType;
  tz?: TZ;
  device_status_id?: string;
  org?: string;
  startDate?: string;
  endDate?: string;
  dateFilterKey?: string;
  create_task?: boolean;
  task_type?: TaskTypes;
  task_status?: TaskStatus;
  timeshift_earliest?: string;
  timeshift_latest?: string;
  timeshift_offset?: string;
  timeshift_source_type?: string;
  timeshift_modify_amount?: number;
  alert_type?: Array<string>;
  auto_refresh_images?: boolean;
  un_blur_images?: boolean;
  view_all_users?: boolean;
}

type SearchParamsUpdater = (currentParams: SearchParams) => SearchParams;

/** useSearch hook to read and write values of any data structure to search params */
export const useSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = React.useMemo(() => {
    const parsed = qs.parse(location.search, PARSE_OPTIONS) as SearchParams;

    return {
      ...parsed,
      tableRows: Array.isArray(parsed.tableRows) ? parsed.tableRows.map((el) => el.toString()) : undefined,
      device: parsed.device?.id
        ? {
            ...parsed.device,
            id: parsed.device.id.toString?.(),
          }
        : undefined,
    };
  }, [location.search]);
  const searchParamsRef = React.useRef<SearchParams>({});

  React.useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  const setSearchParams = React.useCallback(
    (updater: SearchParamsUpdater, shouldReplace?: boolean) => {
      const params = typeof updater === 'function' ? updater(searchParamsRef.current) : updater;
      navigate(
        {
          ...location,
          search: qs.stringify(params, STRINGIFY_OPTIONS),
        },
        {
          replace: shouldReplace,
        }
      );
    },
    [navigate, location]
  );

  return [searchParams as SearchParams, setSearchParams] as const;
};
